/**
 * Send SMS to Farmer Dialog
 * 
 * Allows sending personalized SMS messages to farmers using SMS templates.
 * Features:
 * - Template selection (SMS templates only)
 * - Live character counter (160 char limit)
 * - Message preview with variables
 * - E.164 phone number validation
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Button,
  Select,
  toast
} from '@etherisc/ui-kit';
import { XIcon, MessageSquareIcon } from 'lucide-react';
import type { Farmer } from '../../types/farmer';

interface SmsTemplate {
  id: string;
  name: string;
  description?: string;
}

interface SendSmsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  farmer: Farmer;
}

export function SendSmsDialog({
  isOpen,
  onClose,
  farmer
}: SendSmsDialogProps) {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messagePreview, setMessagePreview] = useState<string>('');
  const [characterCount, setCharacterCount] = useState<number>(0);

  useEffect(() => {
    if (isOpen) {
      loadSmsTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTemplate) {
      generatePreview();
    } else {
      setMessagePreview('');
      setCharacterCount(0);
    }
  }, [selectedTemplate]);

  const loadSmsTemplates = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/document-templates?type=SMS&status=ACTIVE', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load SMS templates');
      }

      const data = await response.json();
      setTemplates(data.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = () => {
    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    // Simple variable substitution for preview
    // In production, this would be done by the backend
    let preview = template.name; // Placeholder - would load actual template content
    
    // Simulate variable replacement
    preview = preview.replace('{{farmerName}}', farmer.name || '');
    preview = preview.replace('{{farmerCode}}', farmer.farmerCode || '');
    preview = preview.replace('{{phoneNumber}}', farmer.phone || '');
    
    setMessagePreview(preview);
    setCharacterCount(preview.length);
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    if (!farmer.phone) {
      toast.error('Farmer has no phone number');
      return;
    }

    try {
      setSending(true);

      const template = templates.find(t => t.id === selectedTemplate);

      // Queue notification
      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json',
          'X-Idempotency-Key': `farmer-sms-${farmer.id}-${Date.now()}`
        },
        body: JSON.stringify({
          channel: 'SMS',
          to: [{
            phoneNumber: farmer.phone,
            name: farmer.name
          }],
          template: {
            name: template?.name || selectedTemplate,
            version: 'v1'
          },
          variables: {
            farmerName: farmer.name,
            farmerCode: farmer.farmerCode,
            phoneNumber: farmer.phone || 'Not provided',
            county: farmer.county || 'Not provided',
            ward: farmer.ward || 'Not specified'
          },
          channelMetadata: {}
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send SMS');
      }

      toast.success('SMS queued for sending', {
        description: `Will be sent to ${farmer.phone}`
      });

      onClose();
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send SMS', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-md mx-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <MessageSquareIcon size={20} />
                Send SMS to {farmer.name}
              </h2>
              <Button variant="outline" size="sm" onClick={onClose}>
                <XIcon size={16} />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Farmer Details */}
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">Recipient</p>
                <p className="font-medium">{farmer.phone || 'No phone number'}</p>
              </div>

              {/* Template Selection */}
              <div>
                <label htmlFor="sms-template" className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Template
                </label>
                {templates.length > 0 ? (
                  <Select
                    id="sms-template"
                    value={selectedTemplate}
                    onValueChange={setSelectedTemplate}
                    options={templates.map(t => ({
                      value: t.id,
                      label: `${t.name}${t.description ? ` - ${t.description}` : ''}`
                    }))}
                    placeholder="Select a template..."
                  />
                ) : (
                  <p className="text-sm text-gray-500 py-2">Loading templates...</p>
                )}
              </div>

              {loading && (
                <p className="text-sm text-gray-500 text-center">Loading templates...</p>
              )}

              {!loading && templates.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    No SMS templates found. Create one in Document Templates first.
                  </p>
                </div>
              )}

              {/* Message Preview & Character Counter */}
              {selectedTemplate && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-blue-900 font-medium">Preview</p>
                      <p className={`text-xs font-mono ${characterCount > 160 ? 'text-red-600 font-bold' : 'text-blue-800'}`}>
                        {characterCount}/160
                      </p>
                    </div>
                    <p className="text-sm text-blue-800">
                      {messagePreview || 'Template preview will appear here...'}
                    </p>
                  </div>

                  {characterCount > 160 && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-sm text-red-800 font-medium">
                        ⚠️ Message exceeds 160 characters and will be truncated
                      </p>
                    </div>
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="text-xs text-gray-700 font-medium mb-1">Variables available:</p>
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      <li>• farmerName, farmerCode, phoneNumber, county, ward</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSend} 
                disabled={!selectedTemplate || !farmer.phone || sending}
              >
                <MessageSquareIcon size={16} className="mr-2" />
                {sending ? 'Sending...' : 'Send SMS'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

